import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { ThemeProvider } from 'styled-components'
import { theme as defaultTheme, Theme } from '@popety-io/ui'
// import { publicApiService } from '../services/publicApiService'

interface ExtendedTheme extends Theme {
  customLogo?: string
  brandName?: string
}

interface CustomThemeContextType {
  theme: ExtendedTheme
  isLoading: boolean
  error: string | null
  updateThemeConfig: (config: CustomThemeConfig | null) => void
}

function createCustomTheme(config: CustomThemeConfig): ExtendedTheme {
  const customTheme: ExtendedTheme = {
    ...defaultTheme,
    colors: {
      ...defaultTheme.colors,
      // Override primary color if provided
      ...(config.colors?.primary && {
        primary: config.colors.primary,
        primaryHover: adjustColorBrightness(config.colors.primary, -10),
        primaryLight: adjustColorBrightness(config.colors.primary, 90),
      }),
      // Override secondary color if provided - map to success color as fallback
      ...(config.colors?.secondary && {
        success: config.colors.secondary,
        successLight: adjustColorBrightness(config.colors.secondary, 70),
      }),
    },
    typography: {
      ...defaultTheme.typography,
      // Override font family if provided
      ...(config.fonts?.primary && {
        fontFamily: {
          ...defaultTheme.typography.fontFamily,
          sans: config.fonts.primary,
        },
      }),
    },
    // Add custom logo and brand name to theme if provided
    ...(config.logo && { customLogo: config.logo }),
    ...(config.brandName && { brandName: config.brandName }),
  }

  return customTheme
}

// Helper function to adjust color brightness
function adjustColorBrightness(hexColor: string, percent: number): string {
  // Remove # if present
  const hex = hexColor.replace('#', '')

  // Parse RGB values
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)

  // Calculate new RGB values
  const newR = Math.max(
    0,
    Math.min(255, Math.round(r + (255 - r) * (percent / 100)))
  )
  const newG = Math.max(
    0,
    Math.min(255, Math.round(g + (255 - g) * (percent / 100)))
  )
  const newB = Math.max(
    0,
    Math.min(255, Math.round(b + (255 - b) * (percent / 100)))
  )

  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
}

/**
 * Dynamically load a Google Font by injecting a <link> tag into <head>.
 * Skips if the font is already loaded.
 */
function loadGoogleFont(fontFamily: string) {
  const fontId = `google-font-${fontFamily.replace(/\s+/g, '-').toLowerCase()}`

  if (document.getElementById(fontId)) return

  const link = document.createElement('link')
  link.id = fontId
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`
  document.head.appendChild(link)
}

const CustomThemeContext = createContext<CustomThemeContextType | undefined>(
  undefined
)

export const useCustomTheme = () => {
  const context = useContext(CustomThemeContext)
  if (!context) {
    throw new Error('useCustomTheme must be used within CustomThemeProvider')
  }
  return context
}

interface CustomThemeProviderProps {
  children: React.ReactNode
}

export interface CustomThemeConfig {
  colors?: {
    primary?: string
    secondary?: string
    accent?: string
    background?: string
    surface?: string
    text?: string
  }
  fonts?: {
    primary?: string
    secondary?: string
  }
  logo?: string
  brandName?: string
}

export const CustomThemeProvider: React.FC<CustomThemeProviderProps> = ({
  children,
}) => {
  const [theme, setTheme] = useState<ExtendedTheme>(defaultTheme)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadCustomTheme = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Extract URL parameters for theme customization
        const urlParams = new URLSearchParams(window.location.search)
        const primaryColor = urlParams.get('primary')
        const secondaryColor = urlParams.get('secondary')
        const fontStyle = urlParams.get('font')

        // Create theme configuration from URL parameters
        const themeConfig: CustomThemeConfig = {}

        if (primaryColor || secondaryColor) {
          themeConfig.colors = {}
          if (primaryColor) {
            // Support both with and without # prefix
            const colorValue = primaryColor.startsWith('#')
              ? primaryColor
              : `#${primaryColor}`
            // Validate and sanitize color (basic validation for hex colors)
            const validHexColor = /^#[0-9A-Fa-f]{6}$/.test(colorValue)
            if (validHexColor) {
              themeConfig.colors.primary = colorValue
            }
          }
          if (secondaryColor) {
            // Support both with and without # prefix
            const colorValue = secondaryColor.startsWith('#')
              ? secondaryColor
              : `#${secondaryColor}`
            // Validate and sanitize color (basic validation for hex colors)
            const validHexColor = /^#[0-9A-Fa-f]{6}$/.test(colorValue)
            if (validHexColor) {
              themeConfig.colors.secondary = colorValue
            }
          }
        }

        if (fontStyle) {
          // Validate font family (basic validation to prevent injection)
          // Allow common font names and families
          const validFontFamily = /^[a-zA-Z\s,'"()-]+$/.test(fontStyle)
          if (validFontFamily) {
            loadGoogleFont(fontStyle)
            themeConfig.fonts = {
              primary: fontStyle,
              secondary: fontStyle,
            }
          }
        }

        // Apply theme customizations if any parameters were provided
        if (Object.keys(themeConfig).length > 0) {
          const customTheme = createCustomTheme(themeConfig)
          setTheme(customTheme)
        } else {
          setTheme(defaultTheme)
        }

        // NOTE: Public ID extraction and API-based theme loading is now handled
        // by the individual public pages using useResolvedThemeColors hook
      } catch (err) {
        console.error('Failed to load custom theme:', err)
        setError('Failed to load theme configuration')
        setTheme(defaultTheme)
      } finally {
        setIsLoading(false)
      }
    }

    loadCustomTheme()

    // Listen for URL changes to update theme dynamically
    const handleLocationChange = () => {
      loadCustomTheme()
    }

    window.addEventListener('popstate', handleLocationChange)
    return () => window.removeEventListener('popstate', handleLocationChange)
  }, [])

  // Allow external callers to update the theme (e.g., after loading company/office data)
  const updateThemeConfig = useCallback((config: CustomThemeConfig | null) => {
    if (config?.colors?.primary) {
      const customTheme = createCustomTheme(config)
      setTheme(customTheme)
    }
  }, [])

  const contextValue = {
    theme,
    isLoading,
    error,
    updateThemeConfig,
  }

  return (
    <CustomThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </CustomThemeContext.Provider>
  )
}
